import { buildCategoryTree } from '../../lib/categoryTree.ts';
import {
  LIBRARY_SORT_COMPARATORS,
  libraryCounts,
  matchesLibraryItem,
  type LibraryCounts,
  type LibrarySearchText,
} from './libraryFilterOptions.ts';
import { libraryActiveFilterCount, type LibraryFilterState } from './libraryFilterState.ts';
import type { CategoryTreeNode } from '../../lib/categoryTree.ts';
import type { CategoryEntry, LibraryItem, LibrarySlide } from '../../../shared/api.ts';

/** Re-exported so the page and its tiles keep importing the sentinel and the
 *  type axis from here; both now live in the URL codec, which has to know
 *  them. */
export { UNCATEGORISED, type LibraryTypeFilter } from './libraryFilterState.ts';
export { isSeries } from './libraryFilterOptions.ts';

type LibraryFiltersInput = {
  /** Each item carries its own `slides`: an item owns a deck, so there is no
   *  page-wide slide list to pick them out of any more. */
  items: LibraryItem[];
  /** The registry in display order, INCLUDING empty categories, and carrying
   *  each category's optional colour — the rail's folder dots and the grouped
   *  body's heading dots (Run 11). */
  categoryOrder: readonly (string | CategoryEntry)[];
  /** The whole view, as one object — see `libraryFilterState.ts`. */
  filters: LibraryFilterState;
  /** The slug of the item open in the editor overlay, if any. */
  editingItem: string | null;
};

export type LibraryFilters = {
  categoryTree: CategoryTreeNode[];
  allCategories: string[];
  /** The whole shelf, for the title row's standing line. */
  counts: LibraryCounts;
  /** What the free-text axis reads per item — handed to the options module so
   *  the menu counts search the same text the body does. */
  slideText: LibrarySearchText;
  /** True while any axis is narrowing — what the chip row and the empty state
   *  both read, so neither has to list the axes itself. */
  filtering: boolean;
  /** Filtered AND ordered: the body renders this list as it comes. */
  filteredItems: LibraryItem[];
  /** The item being edited, and the slide the overlay opens on. Both null
   *  unless an item is open AND it still has a slide. */
  editing: LibraryItem | null;
  editingFirstSlide: LibrarySlide | null;
};

/**
 * Everything the Library page DERIVES from its one fetch plus its filter
 * state. Lifted out of `Library.tsx` on 2026-09-07 (Run 8); rewired onto the
 * one filter object in Run 9, when the two loose `activeCategory` /
 * `typeFilter` states became six axes and a URL.
 *
 * The MATCHING is not here: it lives in `libraryFilterOptions.ts` next door,
 * because the pill menus need each axis on its own and the body needs all of
 * them at once, and one copy answering both is what stops a menu count from
 * promising something the body then doesn't show.
 *
 * Pure and synchronous: no hooks, despite the name matching the decks page's
 * `useDeckFilters`. There is nothing here worth memoising — the whole library
 * is tens of items — and the version that owns no state can be read in one
 * sitting.
 */
export function useLibraryFilters({ items, categoryOrder, filters, editingItem }: LibraryFiltersInput): LibraryFilters {
  const { tree: categoryTree, allCategories } = buildCategoryTree(
    items.map((it) => it.category),
    categoryOrder,
  );

  // The searchable text on an item's SLIDES, built once for the whole page:
  // the listing carries a slide's title and its section name and nothing else
  // of what is written on it, so those are what a search can honestly match.
  const slideTextByItem = new Map<string, string>();
  for (const item of items) {
    const line = item.slides
      .map((slide) => `${slide.title || ''} ${slide.sectionName || ''}`)
      .join(' ')
      .toLowerCase();
    slideTextByItem.set(item.slug, line);
  }
  const slideText: LibrarySearchText = (slug) => slideTextByItem.get(slug) ?? '';

  const editing = editingItem ? (items.find((it) => it.slug === editingItem) ?? null) : null;
  const editingFirstSlide = editing?.slides[0] ?? null;

  return {
    categoryTree,
    allCategories,
    counts: libraryCounts(items),
    slideText,
    filtering: libraryActiveFilterCount(filters) > 0,
    filteredItems: items
      .filter((it) => matchesLibraryItem(it, filters, { slideText }))
      .sort(LIBRARY_SORT_COMPARATORS[filters.sort]),
    editing,
    editingFirstSlide,
  };
}
